import React, { useEffect } from 'react'
import { Tabs, Tab, Box } from '@mui/material'
import { useAppSearchParams } from '../../hooks/useAppSearchParams'
import { SlotStatus } from '../../models/DistributionPlan'

interface ProjectSectionTabProps {
  status: SlotStatus
}

const TAB_DEFS = [
  { label: 'Draft', value: 'draft' },
  { label: 'Live & Upcoming', value: 'live_upcoming' },
  { label: 'Closed', value: 'closed' }
] as const

type TabValue = (typeof TAB_DEFS)[number]['value']

const getVisibleTabs = (status: SlotStatus): ReadonlyArray<(typeof TAB_DEFS)[number]> => {
  if (status === SlotStatus.Closed) {
    return TAB_DEFS.filter(tab => tab.value === 'draft' || tab.value === 'closed')
  }

  return TAB_DEFS
}

export const viewFromStatus = (status: SlotStatus): TabValue => {
  switch (status) {
    case SlotStatus.Live:
    case SlotStatus.Published:
      return 'live_upcoming'
    case SlotStatus.Closed:
      return 'closed'
    default:
      return 'draft'
  }
}

const ProjectSectionTab: React.FC<ProjectSectionTabProps> = ({ status }) => {
  const { searchParams, setUrlParams } = useAppSearchParams()
  const visibleTabs = getVisibleTabs(status)

  const urlView = searchParams.get('view') as TabValue | null
  const fallbackView = viewFromStatus(status)
  const activeView = visibleTabs.some(tab => tab.value === urlView) ? urlView : fallbackView

  useEffect(() => {
    if (activeView !== urlView) setUrlParams({ view: activeView })
  }, [activeView, urlView, setUrlParams])

  // If status is Draft, do not show any tabs
  if (status === SlotStatus.Draft) return null

  const handleChange = (_: React.SyntheticEvent, next: TabValue): void => {
    setUrlParams({ view: next })
  }

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Tabs value={activeView} onChange={handleChange}>
        {visibleTabs.map(t => (
          <Tab key={t.value} label={t.label} value={t.value} sx={{ fontWeight: 600 }} />
        ))}
      </Tabs>
    </Box>
  )
}

export default ProjectSectionTab
